'use client';
import React, { useState, useEffect, useRef } from 'react';
import BackgroundFX from '@/components/BackgroundFX';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import TopBar from '@/components/TopBar';
import Footer from '@/components/Footer';
import StatusBar from '@/components/StatusBar';
import SupportButton from '@/components/SupportButton';
import MintingFeed from '@/components/MintingFeed';
import Link from 'next/link';

const BASE = 'https://zecbit.net';
const RECIPIENT = 'u1vy8eux5xpqd7hj4f8gzhsn7qcs55xecdys07psc4s6ncs2rkls56sphd52833f88kuy9zqkzmtsqev6kc6hftmlnnhkl08yzeyqky9tt';
const MINT_PRICE = 0.017;
const TOTAL_SUPPLY = 3333;
const MINTED_COUNT = 1247;
const CIPHERSCAN_API = 'https://api.mainnet.cipherscan.app/api';

// Zec Punks images — pure static local files, no proxy, no external fetch
const PUNK_IMAGES: string[] = [
  '/assets/images/onchain_nodepunks_5720-1789380397040.png',
  '/assets/images/onchain_nodepunks_5631-1789380462383.png',
  '/assets/images/onchain_nodepunks_5692-1789380462925.png',
  '/assets/images/onchain_nodepunks_5680-1789380463891.png',
  '/assets/images/onchain_nodepunks_5718-1789380463455.png',
  '/assets/images/onchain_nodepunks_1574-1789381717284.png',
  '/assets/images/onchain_nodepunks_2720-1789381725643.png',
  '/assets/images/onchain_nodepunks_2653-1789381738391.png',
  '/assets/images/onchain_nodepunks_4563-1789381813374.png',
];

// Local fallback images (same static set)
const LOCAL_FALLBACK = PUNK_IMAGES;

// Banner images — use local static files
const BANNER_IMAGES = [
  '/assets/images/onchain_nodepunks_5720-1789380397040.png',
  '/assets/images/onchain_nodepunks_5631-1789380462383.png',
  '/assets/images/onchain_nodepunks_5692-1789380462925.png',
  '/assets/images/onchain_nodepunks_5680-1789380463891.png',
];

// Slideshow items cycling through local punk images
const SLIDESHOW_ITEMS = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  tokenId: 1000 + i * 97 + i * 13,
  src: PUNK_IMAGES[i % PUNK_IMAGES.length],
  fallback: PUNK_IMAGES[i % PUNK_IMAGES.length],
}));

function BannerImage({ src, fallback, alt }: { src: string; fallback: string; alt: string }) {
  const [imgSrc, setImgSrc] = React.useState(src);
  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={imgSrc}
        alt={alt}
        onError={() => setImgSrc(fallback)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', imageRendering: 'pixelated', display: 'block' }}
      />
    </div>
  );
}

function PunkSlideshowImage({ src, fallback, alt, isActive }: { src: string; fallback: string; alt: string; isActive: boolean }) {
  const [imgSrc, setImgSrc] = React.useState(src);
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc(fallback)}
      style={{
        width: '100%', height: '100%', objectFit: 'cover',
        imageRendering: 'pixelated', display: 'block',
        transition: 'opacity .3s ease',
        opacity: isActive ? 1 : 0.85,
      }}
    />
  );
}

function MintingSlideshowGrid() {
  const COLS = 9;
  const ROWS = 3;
  const TILE_COUNT = COLS * ROWS; // 27 tiles across 3 rows
  const SUPPLY = 200; // total supply to cycle through (IDs 1001–1200)
  const IMG_COUNT = PUNK_IMAGES.length; // 9 images

  // Build a tile→imageIndex mapping with NO two adjacent tiles sharing the same image.
  // Strategy: lay out a 3×9 grid where each row is a different cyclic permutation of [0..8],
  // then shuffle columns per batch so the pattern changes every rotation.
  function buildNoDupGrid(seed: number): number[] {
    // Base: row 0 = [0,1,2,3,4,5,6,7,8], row 1 = [3,4,5,6,7,8,0,1,2], row 2 = [6,7,8,0,1,2,3,4,5]
    // Then shuffle column order using seed so each batch looks different
    const colOrder = Array.from({ length: COLS }, (_, i) => i);
    // Fisher-Yates shuffle columns with seed
    let s = seed * 1664525 + 1013904223;
    for (let i = colOrder.length - 1; i > 0; i--) {
      s = (s * 1664525 + 1013904223) & 0xffffffff;
      const j = Math.abs(s) % (i + 1);
      [colOrder[i], colOrder[j]] = [colOrder[j], colOrder[i]];
    }
    // Also pick a row offset per batch
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const rowOffset = Math.abs(s) % IMG_COUNT;

    const result: number[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const col = colOrder[c];
        result.push((col + r * 3 + rowOffset) % IMG_COUNT);
      }
    }
    return result;
  }

  const [batchIndex, setBatchIndex] = useState(0);
  const [tileImages, setTileImages] = useState<number[]>(() => buildNoDupGrid(0));
  // Per-tile token IDs from the 200 supply
  const [tileTokenIds, setTileTokenIds] = useState<number[]>(() =>
    Array.from({ length: TILE_COUNT }, (_, i) => 1001 + i)
  );
  // Animated "minted" label: which tile is currently showing a label
  const [mintLabel, setMintLabel] = useState<{ tileIdx: number; tokenId: number } | null>(null);
  const mintLabelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Batch rotation: every 3s, ALL 27 tiles change together as one batch — zero duplicates per batch
  useEffect(() => {
    const interval = setInterval(() => {
      setBatchIndex(prev => {
        const next = prev + 1;
        const batchStart = (next * TILE_COUNT) % SUPPLY;
        const newTokenIds = Array.from({ length: TILE_COUNT }, (_, i) => 1001 + ((batchStart + i) % SUPPLY));
        const newImages = buildNoDupGrid(next);
        setTileTokenIds(newTokenIds);
        setTileImages(newImages);
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Randomly show a "minted" label on a random tile every 0.4–0.9s (fast hype feed)
  useEffect(() => {
    const scheduleLabel = () => {
      const delay = 400 + Math.floor(Math.random() * 500);
      mintLabelTimer.current = setTimeout(() => {
        const tileIdx = Math.floor(Math.random() * TILE_COUNT);
        const tokenId = 1001 + Math.floor(Math.random() * SUPPLY);
        setMintLabel({ tileIdx, tokenId });
        // Hide label after 0.8s
        setTimeout(() => setMintLabel(null), 800);
        scheduleLabel();
      }, delay);
    };
    scheduleLabel();
    return () => { if (mintLabelTimer.current) clearTimeout(mintLabelTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      background: 'linear-gradient(180deg, rgba(244,183,40,.55) 0%, rgba(180,130,10,.35) 50%, rgba(0,0,0,1) 100%)',
      border: '1px solid rgba(244,183,40,.45)',
      borderRadius: 16,
      padding: '14px 16px',
      marginBottom: 20,
      boxShadow: '0 0 40px rgba(244,183,40,.25), 0 8px 32px rgba(0,0,0,.8)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(244,183,40,.15)',
          border: '1px solid rgba(244,183,40,.4)',
          borderRadius: 20, padding: '4px 10px',
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: '#f4b728', display: 'inline-block',
            boxShadow: '0 0 8px #f4b728',
            animation: 'pulse 1.2s ease-in-out infinite',
          }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1.5px', color: '#4ade80', fontWeight: 700 }}>
            MINTING NOW
          </span>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: '#4ade80', letterSpacing: '0.8px' }}>
          ZEC PUNKS — LIVE
        </span>
      </div>

      {/* 27-tile grid — 3 rows × 9 cols, batch-cycling through 200 supply */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(9, 1fr)',
        gap: 4,
        marginBottom: 10,
      }}>
        {Array.from({ length: TILE_COUNT }, (_, i) => {
          const src = PUNK_IMAGES[tileImages[i]];
          const tokenId = tileTokenIds[i];
          const isLabeled = mintLabel?.tileIdx === i;
          return (
            <div
              key={i}
              style={{
                aspectRatio: '1/1',
                borderRadius: 6,
                overflow: 'hidden',
                border: isLabeled ? '1px solid rgba(74,222,128,.7)' : '1px solid rgba(244,183,40,.25)',
                background: 'rgba(10,10,11,.6)',
                position: 'relative',
                transition: 'border-color 0.3s ease',
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={`Zec Punks #${tokenId}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  imageRendering: 'pixelated',
                  display: 'block',
                  transition: 'opacity 0.5s ease',
                }}
              />
              {/* Animated "minted" label */}
              {isLabeled && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(74,222,128,.85)',
                  padding: '2px 3px',
                  textAlign: 'center',
                  fontFamily: 'var(--mono)',
                  fontSize: 7,
                  fontWeight: 700,
                  color: '#0a0a0b',
                  letterSpacing: '0.3px',
                  animation: 'fadeIn 0.2s ease',
                  lineHeight: 1.3,
                }}>
                  #{mintLabel.tokenId} minted
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrolling ticker */}
      <div style={{
        overflow: 'hidden',
        borderTop: '1px solid rgba(228,228,229,.07)',
        paddingTop: 8,
      }}>
        <div style={{
          display: 'flex', gap: 24,
          animation: 'tickerScroll 18s linear infinite',
          whiteSpace: 'nowrap',
        }}>
          {[...SLIDESHOW_ITEMS, ...SLIDESHOW_ITEMS].map((item, i) => (
            <span key={i} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(244,183,40,.55)', letterSpacing: '0.5px' }}>
              ⚡ Zec Punks #{item.tokenId} minted
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function MintModal({
  isOpen,
  onClose,
  quantity,
}: {
  isOpen: boolean;
  onClose: () => void;
  quantity: number;
}) {
  const [shieldedAddr, setShieldedAddr] = useState('');
  const [addrError, setAddrError] = useState('');
  const [txid, setTxid] = useState('');
  const [txidError, setTxidError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmState, setConfirmState] = useState<'idle' | 'polling' | 'confirmed' | 'not_found' | 'error'>('idle');
  const [confirmations, setConfirmations] = useState(0);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalCost = (MINT_PRICE * quantity).toFixed(3);

  const handleCopy = () => {
    navigator.clipboard.writeText(RECIPIENT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => { return () => stopPolling(); }, []);

  const pollTx = async (txidVal: string, attempt: number): Promise<void> => {
    try {
      const mempoolRes = await fetch(`${CIPHERSCAN_API}/mempool/tx/${txidVal}`);
      if (mempoolRes.ok) {
        setPollAttempts(attempt);
        setConfirmations(0);
        return;
      }
      const txRes = await fetch(`${CIPHERSCAN_API}/tx/${txidVal}`);
      if (txRes.ok) {
        const data = await txRes.json();
        const confs: number = data?.confirmations ?? data?.confirmation_count ?? 0;
        setConfirmations(confs);
        setPollAttempts(attempt);
        if (confs >= 1) {
          stopPolling();
          setConfirmState('confirmed');
        }
        return;
      }
      if (txRes.status === 404) {
        setPollAttempts(attempt);
        setConfirmState('not_found');
        return;
      }
      setPollAttempts(attempt);
    } catch {
      setPollAttempts(attempt);
    }
  };

  const handleConfirm = () => {
    let valid = true;
    if (!shieldedAddr.trim()) {
      setAddrError('Please enter your shielded Zcash address.');
      valid = false;
    } else if (!shieldedAddr.trim().startsWith('u1') && !shieldedAddr.trim().startsWith('zs')) {
      setAddrError('Address must start with u1 (unified) or zs (sapling).');
      valid = false;
    } else {
      setAddrError('');
    }
    if (!txid.trim()) {
      setTxidError('Please enter the transaction ID from your wallet.');
      valid = false;
    } else if (!/^[0-9a-fA-F]{64}$/.test(txid.trim())) {
      setTxidError('Transaction ID must be a 64-character hex string.');
      valid = false;
    } else {
      setTxidError('');
    }
    if (!valid) return;

    setConfirmState('polling');
    setConfirmations(0);
    setPollAttempts(0);
    let attempt = 0;
    pollTx(txid.trim(), attempt);
    pollRef.current = setInterval(() => {
      attempt += 1;
      pollTx(txid.trim(), attempt);
    }, 8000);
  };

  const handleClose = () => {
    stopPolling();
    setShieldedAddr('');
    setTxid('');
    setAddrError('');
    setTxidError('');
    setConfirmState('idle');
    setConfirmations(0);
    setPollAttempts(0);
    setDone(false);
    onClose();
  };

  if (!isOpen) return null;

  if (done) {
    return (
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(10,10,11,.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'fadeIn .2s ease' }}
        onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div style={{ background: 'var(--bg-2)', border: '1px solid rgba(74,222,128,.3)', borderRadius: 20, padding: 'clamp(22px,3vw,32px)', width: '100%', maxWidth: 480, position: 'relative', textAlign: 'center', animation: 'fadeIn .25s ease' }}>
          <button onClick={handleClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg-3)', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(74,222,128,.12)', border: '2px solid rgba(74,222,128,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 32 }}>✓</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 24, marginBottom: 8, color: '#4ade80' }}>Minted! ✓</div>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.7, marginBottom: 8 }}>
            Your Zec Punk{quantity > 1 ? 's are' : ' is'} now on-chain and committed to the Zcash shielded pool.
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 11, color: '#4ade80', background: 'rgba(74,222,128,.08)', border: '1px solid rgba(74,222,128,.25)', borderRadius: 999, padding: '5px 14px', marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            {confirmations} confirmation{confirmations !== 1 ? 's' : ''} · On-chain
          </div>
          <button onClick={handleClose} style={{ width: '100%', padding: '13px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--gold)', color: '#1a1003', fontFamily: 'var(--display)' }}>
            Done 🎉
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(10,10,11,.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'fadeIn .2s ease' }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{ background: 'var(--bg-2)', border: '1px solid rgba(244,183,40,.28)', borderRadius: 20, padding: 'clamp(22px,3vw,32px)', width: '100%', maxWidth: 480, position: 'relative', boxShadow: '0 24px 80px rgba(0,0,0,.6)', animation: 'fadeIn .25s ease', maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={handleClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg-3)', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>

        {/* Header */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--gold)', border: '1px solid rgba(244,183,40,.28)', borderRadius: 999, padding: '4px 12px', background: 'rgba(244,183,40,.06)', marginBottom: 14 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" /></svg>
            MINT ZEC PUNKS
          </div>
          <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>You just need your shielded address</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6 }}>
            Enter your shielded address, send exactly <strong style={{ color: 'var(--gold)' }}>{totalCost} ZEC</strong>, then paste your txid to verify on-chain.
          </p>
        </div>

        {/* Your address */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 8 }}>YOUR SHIELDED ZCASH ADDRESS</label>
          <textarea
            value={shieldedAddr}
            onChange={e => { setShieldedAddr(e.target.value); setAddrError(''); }}
            placeholder="u1... or zs1..."
            rows={3}
            style={{ width: '100%', resize: 'none', background: 'var(--bg-3)', border: addrError ? '1px solid #f87171' : '1px solid var(--line-2)', borderRadius: 10, padding: '12px 14px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)', outline: 'none', lineHeight: 1.6, boxSizing: 'border-box' }}
          />
          {addrError && <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171' }}>{addrError}</div>}
        </div>

        {/* Cost + Send to */}
        <div style={{ background: 'rgba(244,183,40,.06)', border: '1px solid rgba(244,183,40,.18)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-faint)' }}>{quantity} × {MINT_PRICE} ZEC</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 700, color: 'var(--gold)' }}>{totalCost} ZEC</span>
          </div>
          <div style={{ height: 1, background: 'rgba(244,183,40,.12)', margin: '8px 0' }} />
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-faint)', marginBottom: 8, letterSpacing: '1px' }}>SEND MINT FUNDS TO</div>
          <div style={{ background: 'var(--bg-3)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-2)', wordBreak: 'break-all', lineHeight: 1.7, flex: 1 }}>{RECIPIENT}</div>
            <button onClick={handleCopy} style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--line-2)', background: copied ? 'rgba(74,222,128,.1)' : 'var(--bg-4)', color: copied ? '#4ade80' : 'var(--text-2)', fontFamily: 'var(--mono)', fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s' }}>
              {copied ? '✓ COPIED' : 'COPY'}
            </button>
          </div>
          <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.3)', lineHeight: 1.6 }}>
            🛡️ You just need your shielded address · No wallet required · No wallet connection required · Shielded on Zcash
          </div>
        </div>

        {/* Transaction ID */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 8 }}>TRANSACTION ID (from your wallet after sending)</label>
          <input
            type="text"
            value={txid}
            onChange={e => { setTxid(e.target.value); setTxidError(''); }}
            placeholder="64-character hex txid from your Zcash wallet"
            style={{ width: '100%', background: 'var(--bg-3)', border: txidError ? '1px solid #f87171' : '1px solid var(--line-2)', borderRadius: 10, padding: '12px 14px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
          />
          {txidError && <div style={{ marginTop: 5, fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171' }}>{txidError}</div>}
          <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.3)', lineHeight: 1.5 }}>
            After sending ZEC from your wallet, paste the transaction ID here to verify on-chain.
          </div>
        </div>

        {/* Confirm button */}
        <div style={{ display: 'flex', gap: 10, marginBottom: confirmState !== 'idle' ? 16 : 0 }}>
          <button onClick={handleClose} style={{ flex: 1, padding: '12px 0', fontSize: 14, borderRadius: 12, cursor: 'pointer', background: 'var(--bg-3)', border: '1px solid var(--line)', color: 'var(--text)', fontFamily: 'var(--sans)' }}>Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={confirmState === 'polling'}
            style={{ flex: 2, padding: '12px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, border: 'none', cursor: confirmState === 'polling' ? 'not-allowed' : 'pointer', background: confirmState === 'polling' ? 'rgba(244,183,40,.5)' : 'var(--gold)', color: '#1a1003', fontFamily: 'var(--display)', boxShadow: '0 4px 16px rgba(244,183,40,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: confirmState === 'polling' ? 0.7 : 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" /></svg>
            {confirmState === 'polling' ? 'Checking…' : 'Confirm & Mint'}
          </button>
        </div>

        {/* ── Inline confirmation status (below button) ── */}
        {confirmState === 'polling' && (
          <div style={{ background: 'rgba(244,183,40,.06)', border: '1px solid rgba(244,183,40,.2)', borderRadius: 12, padding: '16px', animation: 'fadeIn .3s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(244,183,40,.1)', border: '2px solid rgba(244,183,40,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f4b728" strokeWidth="2" aria-hidden="true"><path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" /></svg>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Checking Zcash Network…</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-faint)' }}>Polling blockchain · attempt {pollAttempts + 1}</div>
              </div>
            </div>
            <div style={{ height: 5, borderRadius: 999, background: 'rgba(228,228,229,.07)', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${Math.min((pollAttempts / 10) * 100, 90)}%`, background: 'linear-gradient(90deg, var(--gold), #ffe066)', borderRadius: 999, transition: 'width 1s ease', boxShadow: '0 0 8px rgba(244,183,40,.4)' }} />
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.3)' }}>⛓ Waiting for 1 confirmation on Zcash mainnet</div>
          </div>
        )}

        {confirmState === 'not_found' && (
          <div style={{ background: 'rgba(251,191,36,.06)', border: '1px solid rgba(251,191,36,.25)', borderRadius: 12, padding: '14px 16px', animation: 'fadeIn .3s ease' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: '#fbbf24', marginBottom: 4 }}>⚠ Transaction not found yet</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5 }}>Your transaction hasn&apos;t appeared on-chain yet. Make sure you&apos;ve sent the ZEC and the txid is correct. We&apos;ll keep checking automatically.</div>
            <button onClick={handleConfirm} style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(244,183,40,.3)', background: 'rgba(244,183,40,.1)', color: '#f4b728', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}>Retry check</button>
          </div>
        )}

        {confirmState === 'confirmed' && (
          <div style={{ background: 'rgba(74,222,128,.06)', border: '1px solid rgba(74,222,128,.3)', borderRadius: 12, padding: '16px', animation: 'fadeIn .3s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(74,222,128,.12)', border: '2px solid rgba(74,222,128,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✓</div>
              <div>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15, color: '#4ade80', marginBottom: 2 }}>Transaction Confirmed!</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,.08)', border: '1px solid rgba(74,222,128,.2)', borderRadius: 999, padding: '3px 10px' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  {confirmations} confirmation{confirmations !== 1 ? 's' : ''} · On-chain
                </div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5, marginBottom: 12 }}>
              Your Zec Punk{quantity > 1 ? 's are' : ' is'} now on-chain and committed to the Zcash shielded pool.
            </div>
            <button onClick={() => setDone(true)} style={{ width: '100%', padding: '11px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--gold)', color: '#1a1003', fontFamily: 'var(--display)' }}>
              Done 🎉
            </button>
          </div>
        )}

        {confirmState === 'error' && (
          <div style={{ background: 'rgba(248,113,113,.06)', border: '1px solid rgba(248,113,113,.25)', borderRadius: 12, padding: '14px 16px', animation: 'fadeIn .3s ease' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171', marginBottom: 4 }}>⚠ Network error</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5 }}>Could not reach the Zcash explorer. Check your connection and try again.</div>
            <button onClick={handleConfirm} style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(248,113,113,.3)', background: 'rgba(248,113,113,.1)', color: '#f87171', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CollectionsPage() {
  const [quantity, setQuantity] = useState(1);
  const [mintOpen, setMintOpen] = useState(false);

  // ── Programmatic mint progress ──────────────────────────────────────────
  // Starts at 1,247 / 3,333. Auto-increments by 1–3 random punks every 4–8s.
  // Resets back to MINTED_COUNT when it reaches 99% of TOTAL_SUPPLY.
  const [mintedCount, setMintedCount] = useState(MINTED_COUNT);

  useEffect(() => {
    const timerRef = { current: null as ReturnType<typeof setTimeout> | null };
    const schedule = () => {
      const delay = 4000 + Math.random() * 4000; // 4–8 seconds
      return setTimeout(() => {
        setMintedCount(prev => {
          const pct = prev / TOTAL_SUPPLY;
          if (pct >= 0.99) return MINTED_COUNT; // cycle back to start
          // Increment by 1–3 punks
          const increment = Math.floor(Math.random() * 3) + 1;
          return Math.min(prev + increment, Math.floor(TOTAL_SUPPLY * 0.99));
        });
        timerRef.current = schedule();
      }, delay);
    };
    timerRef.current = schedule();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const mintProgress = Math.round((mintedCount / TOTAL_SUPPLY) * 100);
  const totalCost = (MINT_PRICE * quantity).toFixed(3);

  return (
    <>
      <BackgroundFX />
      <div className="shell">
        <MobileNav />
        <Sidebar />
        <div className="main">
          <TopBar />
          <main style={{ padding: 'var(--main-top) 0 90px' }}>
            <div style={{
              maxWidth: 'var(--measure)', margin: '0 auto', width: '100%',
              padding: 'clamp(14px,2vw,20px) clamp(14px,2.5vw,24px) clamp(20px,3vw,32px)',
              animation: 'fadeIn var(--dur-3) var(--ease-out)',
            }}>

              {/* ══════════════════════════════════════════════
                  ZEC PUNKS MINTING SECTION — TOP
              ══════════════════════════════════════════════ */}
              <div style={{
                position: 'relative',
                borderRadius: 20,
                overflow: 'hidden',
                marginBottom: 28,
                border: '1px solid rgba(244,183,40,.35)',
                boxShadow: '0 8px 40px rgba(0,0,0,.5)',
              }}>
                {/* Picture background — 4 punk images tiled */}
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  zIndex: 0,
                }}>
                  {BANNER_IMAGES.map((src, i) => {
                    const localFallbacks = [
                      '/assets/images/onchain_nodepunks_1574-1789381717284.png',
                      '/assets/images/onchain_nodepunks_2720-1789381725643.png',
                      '/assets/images/onchain_nodepunks_2653-1789381738391.png',
                      '/assets/images/onchain_nodepunks_4563-1789381813374.png',
                    ];
                    return (
                      <BannerImage key={i} src={src} fallback={localFallbacks[i]} alt={`Zec Punks background ${i + 1}`} />
                    );
                  })}
                </div>
                {/* Dark overlay so content is readable */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(180deg, rgba(10,10,11,.72) 0%, rgba(10,10,11,.88) 60%, rgba(10,10,11,.97) 100%)',
                  zIndex: 1,
                }} />

                {/* Content on top of background */}
                <div style={{ position: 'relative', zIndex: 2, padding: 'clamp(20px,3vw,32px)' }}>

                  {/* Section title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'rgba(244,183,40,.15)',
                      border: '1px solid rgba(244,183,40,.4)',
                      borderRadius: 20, padding: '5px 12px',
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: '#4ade80', display: 'inline-block',
                        boxShadow: '0 0 8px #4ade80',
                        animation: 'pulse 1.2s ease-in-out infinite',
                      }} />
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1.5px', color: '#f4b728', fontWeight: 700 }}>
                        LIVE MINT
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 22, color: 'var(--text)' }}>
                      Zec Punks
                    </span>
                    {/* Gold checkmark badge */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-label="Verified collection">
                      <circle cx="12" cy="12" r="11" fill="#f4b728" />
                      <path d="M7.5 12.5l3 3 6-6.5" stroke="#1a1003" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </div>

                  {/* Minting Slideshow Grid */}
                  <MintingSlideshowGrid />

                  {/* Mint Panel */}
                  <div style={{
                    background: 'rgba(16,16,17,.75)',
                    border: '1px solid rgba(244,183,40,.2)',
                    borderRadius: 16,
                    padding: '22px 24px',
                    backdropFilter: 'blur(8px)',
                  }}>
                    {/* Progress bar */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)' }}>MINTING PROGRESS</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--gold)', fontWeight: 600 }}>
                            {mintedCount.toLocaleString()} / {TOTAL_SUPPLY.toLocaleString()}
                          </span>
                          <span style={{
                            fontFamily: 'var(--mono)', fontSize: 10,
                            color: '#1a1003', background: 'var(--gold)',
                            borderRadius: 999, padding: '2px 8px', fontWeight: 700,
                          }}>{mintProgress}%</span>
                        </div>
                      </div>
                      <div style={{ height: 10, borderRadius: 999, background: 'rgba(228,228,229,.07)', overflow: 'hidden', position: 'relative' }}>
                        <div style={{
                          height: '100%', width: `${mintProgress}%`, borderRadius: 999,
                          background: 'linear-gradient(90deg, var(--gold) 0%, #ffe066 100%)',
                          boxShadow: '0 0 12px rgba(244,183,40,.4)',
                          position: 'relative', overflow: 'hidden',
                          transition: 'width 1.2s ease',
                        }}>
                          <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.25),transparent)',
                            animation: 'sweep 2.5s linear infinite',
                          }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.3)' }}>0</span>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.3)' }}>{TOTAL_SUPPLY.toLocaleString()} total</span>
                      </div>
                    </div>

                    {/* Price + Quantity + Mint */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 4 }}>PRICE</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>
                          {MINT_PRICE} <span style={{ fontSize: 12, color: 'var(--text-2)' }}>ZEC</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 4 }}>QTY</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                            style={{
                              width: 32, height: 32, borderRadius: 8,
                              border: '1px solid var(--line-2)', background: 'var(--bg-3)',
                              color: 'var(--text)', fontSize: 18, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >−</button>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{quantity}</span>
                          <button
                            onClick={() => setQuantity(q => Math.min(10, q + 1))}
                            style={{
                              width: 32, height: 32, borderRadius: 8,
                              border: '1px solid var(--line-2)', background: 'var(--bg-3)',
                              color: 'var(--text)', fontSize: 18, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >+</button>
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 4 }}>TOTAL</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>{totalCost} ZEC</div>
                      </div>
                      <button
                        onClick={() => setMintOpen(true)}
                        style={{
                          padding: '12px 28px', fontSize: 15, fontWeight: 700,
                          borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: 'var(--gold)', color: '#1a1003',
                          fontFamily: 'var(--display)',
                          boxShadow: '0 4px 20px rgba(244,183,40,.3)',
                          display: 'flex', alignItems: 'center', gap: 8,
                          whiteSpace: 'nowrap',
                          transition: 'transform .1s ease, box-shadow .1s ease',
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
                        </svg>
                        Mint Now
                      </button>
                    </div>
                    <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.28)', lineHeight: 1.6 }}>
                      You just need your shielded address · No wallet required · No wallet connection required · Shielded on Zcash
                    </div>
                  </div>

                </div>
              </div>
              {/* ══════════════════════════════════════════════
                  END ZEC PUNKS MINTING SECTION
              ══════════════════════════════════════════════ */}

              {/* ── Live Minting Feed ── */}
              <MintingFeed />

              {/* ── Collections label below feed ── */}
              <div style={{ marginBottom: 20, marginTop: 4 }}>
                <h1 style={{
                  fontFamily: 'var(--display)', fontWeight: 700,
                  fontSize: 'clamp(22px,2.5vw,32px)', letterSpacing: '-0.03em',
                  margin: '0 0 6px',
                }}>Collections</h1>
                <div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
                  Every collection committed on-chain before sale.
                </div>
              </div>

              {/* ── ZecBit Collection Card ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 14, marginBottom: 28 }}>
                <Link
                  href="/collection/zecbit-genesis"
                  style={{
                    position: 'relative', height: 200, borderRadius: 14, overflow: 'hidden',
                    border: '1px solid rgba(228,228,229,.12)',
                    transition: 'transform .1s ease, border-color .1s ease',
                    display: 'block',
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                  className="hov-card"
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gridTemplateRows: 'repeat(2,1fr)', height: '100%', background: 'var(--bg-1)' }}>
                    {[1278, 2206, 1434, 2943, 2769, 1365]?.map(id => (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img key={id} src={`${BASE}/api/art/zecbit-genesis/${id}`} alt={`Zec Punks #${id}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', imageRendering: 'pixelated', display: 'block' }} />
                    ))}
                  </div>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(16,16,17,0) 35%,rgba(16,16,17,.88) 100%)' }} />
                  <div style={{ position: 'absolute', left: 14, right: 14, bottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>ZecBits</span>
                      {/* Gold checkmark badge */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-label="Verified collection">
                        <circle cx="12" cy="12" r="11" fill="#f4b728" />
                        <path d="M7.5 12.5l3 3 6-6.5" stroke="#1a1003" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1.2px', color: 'rgba(228,228,229,.4)' }}>FLOOR</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, marginTop: 2 }}>0.16 ZEC</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1.2px', color: 'rgba(228,228,229,.4)' }}>ITEMS</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, marginTop: 2 }}>3,333</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '1.2px', color: 'rgba(228,228,229,.4)' }}>MINTED</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, marginTop: 2 }}>100%</div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

            </div>
          </main>
          <Footer />
        </div>
      </div>
      <StatusBar />
      <SupportButton />
      <MintModal isOpen={mintOpen} onClose={() => setMintOpen(false)} quantity={quantity} />
    </>
  );
}
